/**
 * Hotel Profile top card + package row display SSOT — amount and currency resolved together.
 * Prefers server unified ILS (displayCurrencies / PWCCF) over supplier FinalPrice for agent display.
 */

import { formatHotelSearchAmountForDisplay } from "../constants/hotelCurrencies";
import { formatHotelIlsRoundedDisplay } from "./formatHotelIlsRoundedDisplay";
import { resolveHotelPackageDisplayCurrencies } from "./resolveHotelPackageDisplayCurrencies";
import { getHotelProfilePackageSupplierUsd } from "./hotelSellingPrice";
import { resolveHotelRoomPackageDisplayPrice } from "./resolveHotelRoomPackageDisplayPrice";
import { buildAgentHotelProfilePackageMultiRoomPricingDisplay } from "./pricing/resolveAgentHotelMultiRoomPricingDisplay";
import {
  isAgentHotelCustomerFacingPricingEnabled,
  resolveAgentHotelCustomerFacingPriceIls,
} from "./pricing/resolveAgentHotelCustomerFacingPriceIls";
import { formatCampaignAllotmentPackageDisplay } from "./campaignHotelProfileRooms";

export const HOTEL_PROFILE_DISPLAY_BASE_SOURCE = {
  DISPLAY_CURRENCIES_ILS: "display_currencies.ils",
  OUR_PRICE_PWCCF: "our_price.priceWithCommissionAndCreditCardFee",
  SUPPLIER_FINAL_PRICE: "supplier_final_price",
};

function positiveAmount(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * Unified ILS selling amount from package (same priority as booking SSOT / room cells).
 * @param {object|null|undefined} pkg
 * @returns {{ amount: number, source: string, formattedIls: string|null }|null}
 */
export function extractHotelProfilePackageUnifiedIlsBase(pkg) {
  if (!pkg || typeof pkg !== "object") return null;

  const dc = pkg.displayCurrencies;
  if (dc && typeof dc === "object") {
    const ils = positiveAmount(dc.ils);
    if (ils != null) {
      const formattedIls =
        formatHotelIlsRoundedDisplay(ils) ??
        (typeof dc.formattedIls === "string" && dc.formattedIls.trim() !== ""
          ? dc.formattedIls
          : null);
      return {
        amount: ils,
        source: HOTEL_PROFILE_DISPLAY_BASE_SOURCE.DISPLAY_CURRENCIES_ILS,
        formattedIls,
      };
    }
  }

  const pwccf = positiveAmount(
    pkg?.ourPrice?.priceWithCommissionAndCreditCardFee
  );
  if (pwccf != null) {
    const computed = resolveHotelPackageDisplayCurrencies(pkg);
    return {
      amount: pwccf,
      source: HOTEL_PROFILE_DISPLAY_BASE_SOURCE.OUR_PRICE_PWCCF,
      formattedIls:
        computed?.formattedIls && String(computed.formattedIls).trim() !== ""
          ? computed.formattedIls
          : null,
    };
  }

  return null;
}

/** @param {Array<{ packages?: Array<object> }>|null|undefined} boxes */
export function collectHotelProfilePackagesFromBoxes(boxes) {
  const out = [];
  if (!Array.isArray(boxes)) return out;
  for (const box of boxes) {
    const pkgs = box?.packages;
    if (!Array.isArray(pkgs)) continue;
    for (const pkg of pkgs) {
      if (pkg && typeof pkg === "object") out.push(pkg);
    }
  }
  return out;
}

/**
 * @param {object|null|undefined} pkg
 * @param {object|null|undefined} profilePriceContext
 */
export function resolveHotelProfilePackageDisplayAmount(pkg, profilePriceContext) {
  const campaignDisplay = formatCampaignAllotmentPackageDisplay(pkg, profilePriceContext);
  if (campaignDisplay?.displayLine) return campaignDisplay;
  const rawFinalPrice = getHotelProfilePackageSupplierUsd(pkg);
  const rawOurPriceWithCc =
    pkg?.ourPrice?.priceWithCommissionAndCreditCardFee ?? null;
  const packageId = pkg?.packageID ?? pkg?.PackageId ?? pkg?.packageId ?? null;
  const roomCode =
    pkg?.roomCode ?? pkg?.RoomCode ?? pkg?.Rooms?.[0]?.RoomCode ?? null;

  const packageCustomerTotal = resolveHotelProfilePackageCustomerTotalIls(
    pkg,
    profilePriceContext
  );
  if (
    packageCustomerTotal?.amountIls != null &&
    Number.isFinite(Number(packageCustomerTotal.amountIls))
  ) {
    const chosenDisplayAmount = Number(packageCustomerTotal.amountIls);
    const chosenDisplayCurrency = "ILS";
    const displayLine = formatHotelSearchAmountForDisplay(
      chosenDisplayAmount,
      chosenDisplayCurrency
    );
    return {
      displayLine,
      chosenDisplayAmount,
      chosenDisplayCurrency,
      sourcePath: packageCustomerTotal.sourcePath,
      rawFinalPrice,
      rawOurPriceWithCc,
      rejectedSourceReason: null,
      wasConverted: false,
      sortValue: chosenDisplayAmount,
    };
  }

  if (isAgentHotelCustomerFacingPricingEnabled(profilePriceContext)) {
    const cf = resolveAgentHotelCustomerFacingPriceIls({
      pkg,
      hotelInfo: profilePriceContext?.hotelInfo ?? null,
      profilePriceContext,
      pricingModel: "hotel_profile:agent_hr_display_amount",
    });
    if (cf.pricingStatus === "ok" && cf.priceWithCommission != null) {
      const chosenDisplayAmount = cf.priceWithCommission;
      const chosenDisplayCurrency = cf.displayCurrency ?? "ILS";
      const displayLine =
        cf.displayLine ??
        formatHotelSearchAmountForDisplay(chosenDisplayAmount, chosenDisplayCurrency);
      return {
        displayLine,
        chosenDisplayAmount,
        chosenDisplayCurrency,
        sourcePath: "agent_hr_customer_facing_ssot",
        rawFinalPrice,
        rawOurPriceWithCc,
        rejectedSourceReason: "displayCurrencies_supplier_only_not_customer_final",
        wasConverted: false,
        sortValue: chosenDisplayAmount,
        agentCustomerFacing: cf,
      };
    }
  }

  const unified = extractHotelProfilePackageUnifiedIlsBase(pkg);
  const roomSsot =
    !unified && profilePriceContext
      ? resolveHotelRoomPackageDisplayPrice(pkg, profilePriceContext, {
          packageId,
          roomCode,
        })
      : null;

  if (unified) {
    const chosenDisplayAmount = unified.amount;
    const chosenDisplayCurrency = "ILS";
    const displayLine =
      unified.formattedIls ??
      formatHotelSearchAmountForDisplay(chosenDisplayAmount, chosenDisplayCurrency);

    const tracePayload = {
      hotelId: profilePriceContext?.hotelId ?? null,
      roomCode,
      packageId,
      rawAmount: rawFinalPrice,
      rawCurrency: null,
      selectedDisplayCurrency: chosenDisplayCurrency,
      exchangeRateUsed: null,
      resolvedDisplayAmount: chosenDisplayAmount,
      resolvedDisplayCurrency: chosenDisplayCurrency,
      sourcePath: unified.source,
      wasConverted: false,
      rawFinalPrice,
      rawOurPriceWithCc,
      chosenDisplayAmount,
      chosenDisplayCurrency,
      rejectedSourceReason:
        rawFinalPrice != null
          ? "supplier_finalPrice_not_agent_display_amount"
          : null,
    };

    if (import.meta.env.DEV) {
      try {
        console.info(
          "[HOTEL_PROFILE_TOP_PRICE_CURRENCY_SSOT_TRACE]",
          tracePayload
        );
        console.info("[HOTEL_PROFILE_TOP_CARD_PRICE_SOURCE_TRACE]", {
          rawFinalPrice,
          rawOurPriceWithCc,
          chosenDisplayAmount,
          chosenDisplayCurrency,
          rejectedSourceReason: tracePayload.rejectedSourceReason,
          sourcePath: unified.source,
        });
      } catch (_) {
        /* ignore */
      }
    }

    return {
      displayLine,
      chosenDisplayAmount,
      chosenDisplayCurrency,
      sourcePath: unified.source,
      rawFinalPrice,
      rawOurPriceWithCc,
      rejectedSourceReason: tracePayload.rejectedSourceReason,
      wasConverted: false,
      sortValue: chosenDisplayAmount,
    };
  }

  const agentFxNeedsHr =
    profilePriceContext?.agentHotelPricing?.enabled && roomSsot?.wasConverted;

  if (
    roomSsot?.displayAmount != null &&
    roomSsot.displayCurrency &&
    !agentFxNeedsHr
  ) {
    const chosenDisplayAmount = roomSsot.displayAmount;
    const chosenDisplayCurrency = roomSsot.displayCurrency;
    const displayLine =
      roomSsot.formattedDisplayLine ??
      formatHotelSearchAmountForDisplay(
        chosenDisplayAmount,
        chosenDisplayCurrency
      );

    return {
      displayLine,
      chosenDisplayAmount,
      chosenDisplayCurrency,
      sourcePath: roomSsot.sourceCurrency ?? "room_display_ssot_usd_fx",
      rawFinalPrice,
      rawOurPriceWithCc,
      rejectedSourceReason: null,
      wasConverted: roomSsot.wasConverted,
      sortValue: chosenDisplayAmount,
      packageId,
      roomCode,
      exchangeRateUsed: roomSsot.exchangeRateUsed,
      fallbackUsed: roomSsot.fallbackUsed,
    };
  }

  return {
    displayLine: null,
    chosenDisplayAmount: null,
    chosenDisplayCurrency: null,
    sourcePath: HOTEL_PROFILE_DISPLAY_BASE_SOURCE.SUPPLIER_FINAL_PRICE,
    rawFinalPrice,
    rawOurPriceWithCc,
    rejectedSourceReason: "no_unified_ils_base_use_legacy_pipeline",
    wasConverted: false,
    sortValue: null,
    packageId,
    roomCode,
    _needsLegacyPipeline: true,
  };
}

/**
 * Shared package-level customer-facing total (ILS) resolver for Hotel Profile.
 * For Gimmonix multi-room combo packages, this is the authoritative SSOT used
 * by hero, cards/tables and sorting.
 *
 * @param {object|null|undefined} pkg
 * @param {object|null|undefined} profilePriceContext
 * @param {object|null|undefined} profileRoom
 * @returns {{ amountIls: number|null, sourcePath: string|null }}
 */
export function resolveHotelProfilePackageCustomerTotalIls(
  pkg,
  profilePriceContext,
  profileRoom = null
) {
  if (!pkg || typeof pkg !== "object") {
    return { amountIls: null, sourcePath: null };
  }
  const multiRoom = buildAgentHotelProfilePackageMultiRoomPricingDisplay({
    room: profileRoom,
    pkg,
    profilePriceContext,
  });
  if (
    multiRoom?.isMultiRoom &&
    Number.isFinite(Number(multiRoom?.totalIls)) &&
    Number(multiRoom.totalIls) > 0
  ) {
    return {
      amountIls: Number(multiRoom.totalIls),
      sourcePath: "hotel_profile_combo_package",
    };
  }
  return { amountIls: null, sourcePath: null };
}

/**
 * Cheapest package display for profile hero — boxes/rooms SSOT, not StartingPrice alone.
 * @param {object|null|undefined} hotelInfo
 * @param {object|null|undefined} profilePriceContext
 * @param {Array<object>|null|undefined} [roomPackages] — optional flat package list from rooms UI
 * @returns {ReturnType<typeof resolveHotelProfilePackageDisplayAmount> & { packageId?: string|null }|null}
 */
export function pickCheapestHotelProfilePackageDisplay(
  hotelInfo,
  profilePriceContext,
  roomPackages = null
) {
  const packages = Array.isArray(roomPackages) && roomPackages.length > 0
    ? roomPackages
    : collectHotelProfilePackagesFromBoxes(hotelInfo?.boxes);

  let best = null;
  for (const pkg of packages) {
    const row = resolveHotelProfilePackageDisplayAmount(pkg, profilePriceContext);
    if (row.chosenDisplayAmount == null) continue;
    const pid = pkg?.packageID ?? pkg?.PackageId ?? null;
    if (
      !best ||
      row.chosenDisplayAmount < best.chosenDisplayAmount
    ) {
      best = { ...row, packageId: pid };
    }
  }
  return best;
}
